import { createClient } from '@/lib/supabase/client';

const EDGE_FUNCTION_NAME = 'send-transactional-email';

async function getUserEmail(userId: string): Promise<string> {
  const supabase = createClient();
  const { data } = await supabase
    .from('user_profiles')
    .select('email')
    .eq('id', userId)
    .maybeSingle();
  return data?.email || '';
}

export async function sendBidAcceptedEmail(params: {
  senderId: string;
  senderName: string;
  travelerId: string;
  travelerName: string;
  route: string;
  pickupCity: string;
  destinationCity: string;
  parcelCategory: string;
  weightKg: number;
  offeredPriceKes: number;
  travelDate?: string;
  transportMethod?: string;
  deliveryId: string;
}) {
  try {
    const supabase = createClient();
    const [senderEmail, travelerEmail] = await Promise.all([
      getUserEmail(params.senderId),
      getUserEmail(params.travelerId),
    ]);

    if (!senderEmail && !travelerEmail) return;

    await supabase.functions.invoke(EDGE_FUNCTION_NAME, {
      body: {
        type: 'bid_accepted',
        data: {
          senderEmail,
          senderName: params.senderName,
          travelerEmail,
          travelerName: params.travelerName,
          route: params.route,
          pickupCity: params.pickupCity,
          destinationCity: params.destinationCity,
          parcelCategory: params.parcelCategory,
          weightKg: params.weightKg,
          offeredPriceKes: params.offeredPriceKes,
          travelDate: params.travelDate,
          transportMethod: params.transportMethod,
          deliveryId: params.deliveryId,
          trackingUrl: `${typeof window !== 'undefined' ? window.location.origin : ''}/active-deliveries`,
        },
      },
    });
  } catch (err) {
    console.error('[emailService] sendBidAcceptedEmail failed:', err);
  }
}

export async function sendDeliveryConfirmedEmail(params: {
  senderId: string;
  senderName: string;
  travelerId: string;
  travelerName: string;
  route: string;
  pickupCity: string;
  destinationCity: string;
  parcelCategory: string;
  weightKg: number;
  offeredPriceKes: number;
  deliveryId: string;
}) {
  try {
    const supabase = createClient();
    const [senderEmail, travelerEmail] = await Promise.all([
      getUserEmail(params.senderId),
      getUserEmail(params.travelerId),
    ]);

    if (!senderEmail && !travelerEmail) return;

    await supabase.functions.invoke(EDGE_FUNCTION_NAME, {
      body: {
        type: 'delivery_confirmed',
        data: {
          senderEmail,
          senderName: params.senderName,
          travelerEmail,
          travelerName: params.travelerName,
          route: params.route,
          pickupCity: params.pickupCity,
          destinationCity: params.destinationCity,
          parcelCategory: params.parcelCategory,
          weightKg: params.weightKg,
          offeredPriceKes: params.offeredPriceKes,
          deliveryId: params.deliveryId,
          trackingUrl: `${typeof window !== 'undefined' ? window.location.origin : ''}/accepted-bid-details?id=${params.deliveryId}`,
        },
      },
    });
  } catch (err) {
    console.error('[emailService] sendDeliveryConfirmedEmail failed:', err);
  }
}

export async function sendEscrowReleasedEmail(params: {
  senderId: string;
  senderName: string;
  travelerId: string;
  travelerName: string;
  route: string;
  pickupCity: string;
  destinationCity: string;
  parcelCategory: string;
  weightKg: number;
  offeredPriceKes: number;
  mpesaRef: string;
  deliveryId: string;
}) {
  try {
    const supabase = createClient();
    const [senderEmail, travelerEmail] = await Promise.all([
      getUserEmail(params.senderId),
      getUserEmail(params.travelerId),
    ]);

    if (!senderEmail && !travelerEmail) return;

    await supabase.functions.invoke(EDGE_FUNCTION_NAME, {
      body: {
        type: 'escrow_released',
        data: {
          senderEmail,
          senderName: params.senderName,
          travelerEmail,
          travelerName: params.travelerName,
          route: params.route,
          pickupCity: params.pickupCity,
          destinationCity: params.destinationCity,
          parcelCategory: params.parcelCategory,
          weightKg: params.weightKg,
          offeredPriceKes: params.offeredPriceKes,
          mpesaRef: params.mpesaRef,
          deliveryId: params.deliveryId,
          trackingUrl: `${typeof window !== 'undefined' ? window.location.origin : ''}/accepted-bid-details?id=${params.deliveryId}`,
        },
      },
    });
  } catch (err) {
    console.error('[emailService] sendEscrowReleasedEmail failed:', err);
  }
}

export async function sendMpesaPaymentSuccessEmail(params: {
  userId: string;
  userName: string;
  orderId: string;
  amount: number;
  mpesaRef: string;
  route?: string;
}) {
  try {
    const supabase = createClient();
    const recipientEmail = await getUserEmail(params.userId);
    if (!recipientEmail) return;

    await supabase.functions.invoke(EDGE_FUNCTION_NAME, {
      body: {
        type: 'mpesa_payment_success',
        data: {
          recipientEmail,
          recipientName: params.userName,
          orderId: params.orderId,
          amount: params.amount,
          mpesaRef: params.mpesaRef,
          route: params.route || '',
          nextStepsUrl: `${typeof window !== 'undefined' ? window.location.origin : ''}/marketplace`,
        },
      },
    });
  } catch (err) {
    console.error('[emailService] sendMpesaPaymentSuccessEmail failed:', err);
  }
}

export async function sendOrderCreatedEmail(params: {
  userId: string;
  userName: string;
  orderId: string;
  route?: string;
  parcelCategory?: string;
  weightKg?: number;
  budgetKes?: number;
  deadline?: string;
}) {
  try {
    const supabase = createClient();
    const recipientEmail = await getUserEmail(params.userId);
    if (!recipientEmail) return;

    await supabase.functions.invoke(EDGE_FUNCTION_NAME, {
      body: {
        type: 'order_created',
        data: {
          recipientEmail,
          recipientName: params.userName,
          orderId: params.orderId,
          route: params.route || '',
          parcelCategory: params.parcelCategory || '',
          weightKg: params.weightKg || '',
          budgetKes: params.budgetKes || '',
          deadline: params.deadline || '',
        },
      },
    });
  } catch (err) {
    console.error('[emailService] sendOrderCreatedEmail failed:', err);
  }
}

export async function sendSignupConfirmationEmail(params: {
  email: string;
  name: string;
  role: string;
}) {
  try {
    const supabase = createClient();
    await supabase.functions.invoke(EDGE_FUNCTION_NAME, {
      body: {
        type: 'signup_confirmation',
        data: {
          recipientEmail: params.email,
          recipientName: params.name,
          role: params.role,
        },
      },
    });
  } catch (err) {
    console.error('[emailService] sendSignupConfirmationEmail failed:', err);
  }
}
